// Site-wide values. These mirror the public config at the top of the original
// static site's site.js, and the server-side contract in the quintara-contact
// backend (ContactValidator.php). Only PUBLIC values live here — the Turnstile
// SECRET key and the mail password stay on the backend, never in the frontend.
export const CONTACT_EMAIL = "info@quintaratech.com";
export const CONTACT_PHONE = "+971 55 803 9305";

// The quintara_website/apps/api FastAPI service (Cloud Run), ending in
// /contact. It handles origin allowlisting, rate limiting, honeypot/timing
// checks, server-side Turnstile verification, and sending the enquiry email.
// Overridable per environment via VITE_CONTACT_API_URL (e.g. a localhost
// backend in dev).
export const CONTACT_API_URL =
  import.meta.env.VITE_CONTACT_API_URL ??
  "https://quintara-website-api-685510445420.us-central1.run.app/contact";

// Base URL of the CMS API (same quintara_website/apps/api service). Pages
// fetch their published content from `${CMS_API_URL}/cms/pages/{slug}` at
// runtime, falling back to baked-in defaults when it's unreachable.
export const CMS_API_URL =
  import.meta.env.VITE_CMS_API_URL ??
  "https://quintara-website-api-685510445420.us-central1.run.app";

// Careers still posts to the legacy quintara-contact service's /careers
// endpoint (multipart, with the CV file) — NOT derived from CONTACT_API_URL
// anymore, since the new quintara_website/apps/api service has no /careers
// route yet. Revisit once careers is ported over alongside the CMS work.
export const CAREERS_API_URL =
  import.meta.env.VITE_CAREERS_API_URL ??
  "https://quintara-contact-685510445420.me-central1.run.app/careers";

// Cloudflare Turnstile PUBLIC site key. Safe to ship to the browser; the
// matching SECRET key lives only on the contact backend.
export const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "0x4AAAAAAFPUWdChpkJtblPB";

// Floating WhatsApp widget (shown sitewide). Digits only, country code first,
// no "+", no spaces, no leading zeros. The number still gates whether the
// widget renders at all, but the CTA opens WHATSAPP_LINK (the official
// click-to-chat short link) rather than a wa.me/<digits> URL.
export const WHATSAPP_NUMBER = "971521328763";
export const WHATSAPP_MESSAGE =
  "Hello Quintara, I would like to know more about your WhatsApp business messaging solutions.";
// Official WhatsApp click-to-chat short link for Quintara.
export const WHATSAPP_LINK = "https://wa.me/message/CLB6LWCRYQWXN1";

// Careers form option lists — MUST match the backend CareersValidator.php
// allow-lists exactly (same spelling and order); the server rejects others.
export const TEAMS = [
  "Platform and backend engineering",
  "AI and conversational design",
  "Solutions engineering and onboarding",
  "Enterprise and government sales",
  "Agency partnerships",
  "Operator partnerships",
  "Customer success",
  "Quality, policy and trust",
  "Finance, legal and operations",
  "Other",
];
export const EXPERIENCE = ["Less than 2", "2 to 5", "5 to 10", "10 to 15", "More than 15"];
export const LOCATIONS = [
  "Dubai, UAE",
  "Riyadh, Saudi Arabia",
  "Doha, Qatar",
  "Beirut, Lebanon",
  "Cairo, Egypt",
  "Casablanca, Morocco",
  "Lagos, Nigeria",
  "Istanbul, Turkey",
  "London, UK",
  "Remote",
  "Open to relocation",
];
export const NOTICE_PERIODS = ["Available now", "Up to 1 month", "1 to 2 months", "3 months or more"];
export const LANGUAGES = ["Arabic", "English", "French", "Turkish", "Other"];

// Option lists for the contact form's <select>s. These MUST match the backend's
// ContactValidator.php allow-lists exactly (same spelling and order) — the
// server rejects any value not in these lists.
export const SEGMENTS = [
  "An enterprise",
  "A government entity",
  "An agency",
  "A mobile operator",
  "A small or medium business",
  "A technology partner",
  "Other",
];

export const VOLUMES = [
  "Under 10,000",
  "10,000 to 100,000",
  "100,000 to 1 million",
  "1 to 10 million",
  "Over 10 million",
  "Not sure",
];

export const COUNTRIES = [
  "Afghanistan", "Åland Islands", "Albania", "Algeria", "American Samoa", "Andorra", "Angola", "Anguilla",
  "Antigua and Barbuda", "Argentina", "Armenia", "Aruba", "Australia", "Austria", "Azerbaijan", "Bahamas",
  "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bermuda", "Bhutan", "Bolivia",
  "Bonaire, Sint Eustatius and Saba", "Bosnia and Herzegovina", "Botswana", "Brazil", "British Virgin Islands",
  "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada",
  "Cayman Islands", "Central African Republic", "Chad", "Chile", "China", "Christmas Island",
  "Cocos (Keeling) Islands", "Colombia", "Comoros", "Congo", "Cook Islands", "Costa Rica", "Croatia", "Cuba",
  "Curaçao", "Cyprus", "Czechia", "Democratic Republic of the Congo", "Denmark", "Djibouti", "Dominica",
  "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia",
  "Eswatini", "Ethiopia", "Falkland Islands", "Faroe Islands", "Fiji", "Finland", "France", "French Guiana",
  "French Polynesia", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Gibraltar", "Greece", "Greenland",
  "Grenada", "Guadeloupe", "Guam", "Guatemala", "Guernsey", "Guinea", "Guinea-Bissau", "Guyana", "Haiti",
  "Honduras", "Hong Kong", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Isle of Man",
  "Israel", "Italy", "Ivory Coast", "Jamaica", "Japan", "Jersey", "Jordan", "Kazakhstan", "Kenya", "Kiribati",
  "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein",
  "Lithuania", "Luxembourg", "Macao", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta",
  "Marshall Islands", "Martinique", "Mauritania", "Mauritius", "Mayotte", "Mexico", "Micronesia", "Moldova",
  "Monaco", "Mongolia", "Montenegro", "Montserrat", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru",
  "Nepal", "Netherlands", "New Caledonia", "New Zealand", "Nicaragua", "Niger", "Nigeria", "Niue",
  "Norfolk Island", "North Korea", "North Macedonia", "Northern Mariana Islands", "Norway", "Oman", "Pakistan",
  "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Pitcairn", "Poland",
  "Portugal", "Puerto Rico", "Qatar", "Réunion", "Romania", "Russia", "Rwanda", "Saint Barthélemy",
  "Saint Helena, Ascension and Tristan da Cunha", "Saint Kitts and Nevis", "Saint Lucia",
  "Saint Martin (French part)", "Saint Pierre and Miquelon", "Saint Vincent and the Grenadines", "Samoa",
  "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone",
  "Singapore", "Sint Maarten (Dutch part)", "Slovakia", "Slovenia", "Solomon Islands", "Somalia",
  "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname",
  "Svalbard and Jan Mayen", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand",
  "Timor-Leste", "Togo", "Tokelau", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan",
  "Turks and Caicos Islands", "Tuvalu", "U.S. Virgin Islands", "Uganda", "Ukraine", "United Arab Emirates",
  "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
  "Wallis and Futuna", "Yemen", "Zambia", "Zimbabwe", "Other",
];
